import React, { type CSSProperties } from 'react';
import { SearchModes } from '@/domainLogic/index.ts';
import dynamic from 'next/dynamic';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { SortValues } from '@/app/common/memsearch/types.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { getShowOvernightFilter } from '@/app/common/filters/groups/getShowOvernightFilter.ts';
import { filterAvailability } from '@/app/common/filters/groups/stripUnavailableParams.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { RouteType } from '../../../Curiosity';
import { isLiveSearchByDate } from '../../../utils/api/isLiveSearchByDate';
import FilterContainer from '../verticalFilters/FilterContainer';
import { LOCAL_STORAGE_KEY, localStorageExists } from '../../../utils/localStorage';
import type { Localization } from '../../../common/Localization';
import { hasAirportsOnly } from '../../../utils/productsResponse';
import SustainabilityBanner from '../../Banners/SustainabilityBanner';
import { isSustainabilityAvailable } from '../../Banners/SustainabilityBanner/sustainabilityUtils';
import { getIsPinkPeopleEnabled } from '../../../pinkpeople/utils.ts';

const SustainabilityModal = dynamic(() => import('../../Modal/Modals/SustainabilityModal'), { ssr: false });

type GetPillsOutput = {
  text: string | React.ReactNode;
  overlayTitle?: string;
  control?: FilterContainer;
  iconLeft?: string;
  iconRight?: string;
  hasOverlay: boolean;
  hasCta?: boolean;
  size: number;
  width?: number;
  style?: CSSProperties;
  filterName: string;
  selected?: boolean;
  CustomModal?: React.ComponentType<{ isOpen: boolean; onClose: () => void }>;
  showMapButtonMobile?: boolean;
  routeType?: RouteType;
};

const getPills = ({
  commonData,
  localization,
  searchParams,
  searchInfoData,
  isDesktop,
  productsData,
  isWaiting,
  listView,
  routeType,
  handleShowFilters,
  hasPreselectedFlight,
  t,
}: {
  commonData: CommonData;
  localization: Localization;
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  isDesktop: boolean;
  productsData: ProductsData;
  isWaiting: boolean;
  listView: boolean | null;
  routeType: RouteType;
  handleShowFilters: (val: boolean) => void;
  hasPreselectedFlight: boolean;
  t: TranslationFunction;
}): GetPillsOutput[] => {
  const isTransportEnabled = productsData?.transport?.usecaseEnabled;
  const airports = productsData?.transport?.airports;
  const hasMultipleAirports = Array.isArray(airports) && airports.length > 1;
  const pinkPeoplePillEnabled = localization?.configValuesApp?.pinkPeoplePillEnabled;
  const searchResultsPage = routeType === RouteType.SEARCHRESULTS;
  const osMapPage = routeType === RouteType.OSMAP;

  const offers = filterAvailability({ searchParams, searchInfoData, localization });

  const showDestinationAirportsPill =
    isDesktop &&
    ((isTransportEnabled && hasMultipleAirports) ||
      (isTransportEnabled &&
        canUseDOM &&
        isWaiting &&
        localStorageExists(LOCAL_STORAGE_KEY.PREFILTER_DESTINATION_AIRPORT))) &&
    offers(FILTER_NAME.DESTINATION_AIRPORT);
  const showMapButtonMobile =
    osMapPage || (searchResultsPage && !isLiveSearchByDate(searchParams?.[URLParameters.DATE_FROM]));

  const showOvernightFilter =
    getShowOvernightFilter({ localization, transport: productsData?.transport, searchParams }) &&
    searchParams[URLParameters.SEARCH_MODE] === SearchModes.DP &&
    offers(FILTER_NAME.OVERNIGHT_FLIGHTS);
  const sortPill = {
    text:
      t('vacation.oss.components.searchresults.sort.title') !== ''
        ? t('vacation.oss.components.searchresults.sort.title')
        : t('vacation.oss.components.searchresults.sort.sortby'),
    overlayTitle: t('vacation.oss.components.searchresults.sort.sortby'),
    control: FilterContainer.SortContainer,
    filterName: URLParameters.SORT,
    iconLeft: 'sort',
    hasOverlay: true,
    hasCta: false,
    size: 20,
    width: 280,
    selected: searchParams[URLParameters.SORT] && searchParams[URLParameters.SORT] !== SortValues.Recommended,
    showMapButtonMobile,
  };
  const filtersPill = {
    text:
      isDesktop || commonData.isDesktopDevice ? t('vacation.oss.filters.allfilters') : t('vacation.oss.filters.title'),
    control: FilterContainer.AllFiltersContainer,
    iconLeft: 'all_filters',
    filterName: 'all_filters',
    hasOverlay: false,
    size: 20,
    showMapButtonMobile,
    handleShowFilters,
  };

  const commonProps = {
    iconRight: 'arrow_small',
    size: 20,
    hasOverlay: true,
    hasCta: true,
    isHorizontal: true,
  };

  const isPinkPeopleEnabled = getIsPinkPeopleEnabled({
    searchInfoData,
    commonData,
    localization,
  });

  const showPinkPeople = isPinkPeopleEnabled && pinkPeoplePillEnabled;

  const desktopPills = [
    ...(isSustainabilityAvailable({ localization, productsData, searchParams, hasPreselectedFlight })
      ? [
          {
            control: FilterContainer.SustainabilityContainer,
            filterName: 'sustainability',
            text: <SustainabilityBanner isPill={true} />,
            hasOverlay: false,
            CustomModal: SustainabilityModal,
          },
        ]
      : []),
    ...(showPinkPeople
      ? [
          {
            control: FilterContainer.PinkPeopleContainer,
            filterName: URLParameters.PINK_PEOPLE,
            text: 'Pink People', // TODO: get from Smartext?
            ...commonProps,
            selected: !!searchParams[URLParameters.PINK_PEOPLE],
            hasOverlay: false,
            hasCta: false,
            iconRight: null,
          },
        ]
      : []),
    {
      text: t('vacation.oss.filters.budget.title'),
      control: FilterContainer.PriceContainer,
      filterName: URLParameters.PRICE,
      selected: !!searchParams[URLParameters.PRICE],
      ...commonProps,
    },
    ...(showDestinationAirportsPill
      ? [
          {
            text: hasAirportsOnly(productsData)
              ? t('vacation.oss.filters.destination_airport.title')
              : t('vacation.oss.filters.destination_airport_station.title'),
            control: FilterContainer.DestinationAirportContainer,
            filterName: URLParameters.PRE_FILTER_DESTINATION_AIRPORT,
            selected: !!searchParams[URLParameters.PRE_FILTER_DESTINATION_AIRPORT],
            ...commonProps,
          },
        ]
      : []),
    ...(showOvernightFilter
      ? [
          {
            text: t('vacation.oss.overnight_flight.filter.label'),
            control: FilterContainer.OvernightFlightsContainer,
            filterName: URLParameters.OVERNIGHT_FLIGHTS,
            selected: !!searchParams[URLParameters.OVERNIGHT_FLIGHTS],
            ...commonProps,
          },
        ]
      : []),
    {
      text: t('vacation.search.filter.accommodation.type.title'),
      control: FilterContainer.PropertyTypeContainer,
      filterName: URLParameters.ACCOMODATION_TYPE,
      selected: !!searchParams[URLParameters.ACCOMODATION_TYPE],
      ...commonProps,
    },
    {
      text: t('vacation.oss.filters.stars.title'),
      control: FilterContainer.StarsContainer,
      filterName: URLParameters.STARS,
      selected: !!searchParams[URLParameters.STARS],
      ...commonProps,
    },
    {
      text: t('vacation.oss.components.searchfilters.group.rating'),
      control: FilterContainer.RatingContainer,
      filterName: URLParameters.RATING,
      selected: !!searchParams[URLParameters.RATING],
      ...commonProps,
    },
    {
      text: t('vacation.oss.components.searchfilters.group.meal'),
      control: FilterContainer.MealContainer,
      filterName: URLParameters.MEAL,
      selected: !!searchParams[URLParameters.MEAL],
      ...commonProps,
    },
    {
      text: t('vacation.search.filters.facilities.title'),
      control: FilterContainer.FacilitiesContainer,
      filterName: URLParameters.ACCOMODATION_FACILITIES,
      selected:
        !!searchParams[URLParameters.GENERAL_ACCOMODATION_FACILITIES] ||
        !!searchParams[URLParameters.FAMILY_ACCOMODATION_FACILITIES] ||
        !!searchParams[URLParameters.ACTIVITY_ACCOMODATION_FACILITIES] ||
        !!searchParams[URLParameters.WELLNESS_ACCOMODATION_FACILITIES] ||
        !!searchParams[URLParameters.FOOD_ACCOMODATION_FACILITIES],
      ...commonProps,
    },
    filtersPill,
    sortPill,
  ];

  const mobilePills = [
    sortPill,
    filtersPill,
    ...(showMapButtonMobile
      ? [
          {
            text:
              listView || searchResultsPage
                ? t('vacation.oss.map.view_map_short')
                : t('vacation.oss.map.view_list_short'),
            filterName: 'map_toggle',
            iconLeft: listView || searchResultsPage ? 'view_map' : 'view_list',
            hasOverlay: false,
            size: 20,
            showMapButtonMobile,
            routeType,
          },
        ]
      : []),
  ];

  // TODO: text should accept Element as type
  // @ts-ignore
  return isDesktop ? desktopPills : mobilePills || [];
};

export default getPills;
